<template>
  <div class="modal fade" id="modalRecursosPortais" tabindex="-1" aria-labelledby="recursosPortaisTitulo" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
      <div class="modal-content rp-content">
        <div class="modal-header rp-header">
          <div>
            <h5 class="modal-title" id="recursosPortaisTitulo">Recursos disponíveis</h5>
            <p class="rp-sub">O que você pode fazer em cada portal no Lance Fácil</p>
          </div>
          <AjudaBotao :slug="AJUDA_SLUGS.SISTEMA_RECURSOS_DISPONIVEIS" variante="popup" titulo="Ajuda desta janela" />
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
        </div>

        <div class="modal-body rp-body">
          <!-- Telas largas: portais nas linhas, recursos nas colunas -->
          <div class="rp-table-wrap d-none d-md-block">
            <table class="rp-table">
              <thead>
                <tr>
                  <th class="rp-th-portal" scope="col">Portal</th>
                  <th v-for="r in recursos" :key="r.key" scope="col" class="rp-th" :title="r.desc">
                    <span class="rp-th-titulo">{{ r.titulo }}</span>
                    <small class="rp-th-desc">{{ r.desc }}</small>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="l in linhas" :key="l.chave ?? l.id">
                  <th scope="row" class="rp-portal">
                    <span class="rp-dot" :style="{ background: l.cor }"></span>{{ l.nome }}
                  </th>
                  <td v-for="r in recursos" :key="r.key" class="rp-cell">
                    <div class="rp-plat">
                      <small class="rp-plat-nome">Web</small>
                      <RecursoSelo :status="statusWeb(l, r.key)" :rotulo="r.titulo + ' (web)'" />
                    </div>
                    <div v-if="statusDesktop(l, r.key)" class="rp-plat">
                      <small class="rp-plat-nome">Desktop</small>
                      <RecursoSelo :status="statusDesktop(l, r.key)" :rotulo="r.titulo + ' (desktop)'" />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Celular: um cartão por portal -->
          <div class="d-md-none">
            <div v-for="l in linhas" :key="l.chave ?? l.id" class="rp-card">
              <div class="rp-card-titulo"><span class="rp-dot" :style="{ background: l.cor }"></span>{{ l.nome }}</div>
              <ul class="rp-card-lista">
                <li v-for="r in recursos" :key="r.key">
                  <span class="rp-card-nome">{{ r.titulo }}</span>
                  <span class="rp-card-selos">
                    <span class="rp-plat">
                      <small class="rp-plat-nome">Web</small>
                      <RecursoSelo :status="statusWeb(l, r.key)" :rotulo="r.titulo + ' (web)'" />
                    </span>
                    <span v-if="statusDesktop(l, r.key)" class="rp-plat">
                      <small class="rp-plat-nome">Desktop</small>
                      <RecursoSelo :status="statusDesktop(l, r.key)" :rotulo="r.titulo + ' (desktop)'" />
                    </span>
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div class="rp-legenda">
            <span><RecursoSelo status="sim" /> Disponível</span>
            <span><RecursoSelo status="breve" /> Em fase de testes</span>
            <span><RecursoSelo status="nao" /> Ainda não disponível</span>
          </div>
          <p class="rp-sub mt-3 mb-1"><strong>Web</strong> é este sistema, no navegador. <strong>Desktop</strong> é o programa instalado no computador.
            Alguns recursos chegam primeiro ao Desktop; onde não aparece o selo do Desktop, a informação ainda não foi publicada.</p>
          <p class="rp-sub mt-3 mb-1">{{ notaCadastroCasosEspeciais }}</p>
          <p class="rp-sub mt-3 mb-1">{{ notaLicitacoesE }}</p>
          <p class="rp-sub mt-3 mb-0">{{ notaProdutos }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import AjudaBotao from '@/components/AjudaBotao.vue';
import { AJUDA_SLUGS } from '@/config/ajudaEngage';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import eventBus from '@/views/eventBus';
import { recursos, portais, statusRecurso, notaLicitacoesE, notaCadastroCasosEspeciais, notaProdutos } from '@/config/recursosPortais';
import { listaRecursosDisponiveis } from '@/stores/portaisStore';
import RecursoSelo from '@/components/layout/RecursoSelo.vue';

// Linhas vindas da API (configuradas pelo ADM, com Web e Desktop). Se a API não responder, a tela cai no arquivo
// config/recursosPortais.js (só Web) e continua funcionando.
const linhasApi = ref(null);

const linhas = computed(() => linhasApi.value ?? portais.map(p => ({
  id: p.id,
  chave: p.chave,
  nome: p.nome,
  cor: p.cor,
  web: Object.fromEntries(recursos.map(r => [r.key, statusRecurso(r.key, p.chave ?? p.id)])),
  desktop: {},
})));

const statusWeb = (l, chave) => l.web?.[chave] ?? 'nao';
const statusDesktop = (l, chave) => l.desktop?.[chave] ?? null;

const carregar = async () => {
  try {
    const resp = await listaRecursosDisponiveis();
    if (resp?.ok && Array.isArray(resp.data) && resp.data.length) linhasApi.value = resp.data;
  } catch (e) {
    // mantém o arquivo local
  }
};

// Abre a tela quando alguém (menu do topo ou lateral) emite o evento
const abrir = () => {
  const el = document.getElementById('modalRecursosPortais');
  if (el) window.bootstrap.Modal.getOrCreateInstance(el).show();
  carregar();
};

onMounted(() => eventBus.on('abrir-recursos-portais', abrir));
onUnmounted(() => eventBus.off('abrir-recursos-portais', abrir));
</script>

<style scoped>
.rp-content {
  --rp-bg: #fff;
  --rp-text: #3b3f5c;
  --rp-muted: #888ea8;
  --rp-border: #e0e6ed;
  --rp-head: #f8f9fa;
  --rp-row-hover: #f1f4ff;
  --rp-ok-bg: rgba(26, 188, 156, 0.14);
  --rp-ok: #0f9a80;
  --rp-soon-bg: rgba(226, 160, 63, 0.18);
  --rp-soon: #a86a0b;
  --rp-none: #adb5bd;
  background: var(--rp-bg);
  color: var(--rp-text);
  border: 1px solid var(--rp-border);
  border-radius: 12px;
}
.dark .rp-content {
  --rp-bg: #0e1726;
  --rp-text: #e0e6ed;
  --rp-muted: #888ea8;
  --rp-border: #253159;
  --rp-head: #1a2036;
  --rp-row-hover: #131f36;
  --rp-ok-bg: rgba(26, 188, 156, 0.18);
  --rp-ok: #3ddbb5;
  --rp-soon-bg: rgba(226, 160, 63, 0.2);
  --rp-soon: #f0b45a;
  --rp-none: #5b6280;
}

.rp-header {
  border-bottom: 1px solid var(--rp-border);
  align-items: flex-start;
  padding: 18px 22px;
}
.rp-header .modal-title {
  margin: 0;
  font-weight: 700;
  color: var(--rp-text);
}
.rp-sub {
  margin: 2px 0 0;
  font-size: 0.85rem;
  color: var(--rp-muted);
}
.rp-body {
  padding: 18px 22px 22px;
}

/* ── Tabela ── */
.rp-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--rp-border);
  border-radius: 10px;
}
.rp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.rp-th-portal,
.rp-th {
  background: var(--rp-head);
  border-bottom: 1px solid var(--rp-border);
  padding: 12px 10px;
  vertical-align: bottom;
  color: var(--rp-text);
  font-weight: 600;
  text-align: center;
}
.rp-th-portal {
  text-align: left;
  min-width: 190px;
  padding-left: 16px;
}
.rp-th-titulo {
  display: block;
  font-size: 0.82rem;
}
.rp-th-desc {
  display: block;
  margin-top: 4px;
  font-weight: 400;
  font-size: 0.7rem;
  line-height: 1.25;
  color: var(--rp-muted);
}
.rp-table tbody tr {
  transition: background 0.12s;
}
.rp-table tbody tr:hover {
  background: var(--rp-row-hover);
}
.rp-portal {
  padding: 11px 10px 11px 16px;
  font-weight: 600;
  color: var(--rp-text);
  white-space: nowrap;
  border-top: 1px solid var(--rp-border);
  text-align: left;
}
.rp-cell {
  text-align: center;
  padding: 8px 6px;
  border-top: 1px solid var(--rp-border);
}
.rp-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 9px;
}

/* ── Selos ── */
.rp-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 26px;
  padding: 0 9px;
  border-radius: 13px;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
}
.rp-chip--sim {
  background: var(--rp-ok-bg);
  color: var(--rp-ok);
}
.rp-chip--breve {
  background: var(--rp-soon-bg);
  color: var(--rp-soon);
}
.rp-chip--nao {
  color: var(--rp-none);
  font-weight: 400;
}

.rp-plat {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 2px 0;
}
.rp-plat-nome {
  min-width: 48px;
  text-align: right;
  font-size: 0.65rem;
  color: var(--rp-muted);
}
.rp-card-selos {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

/* ── Celular ── */
.rp-card {
  border: 1px solid var(--rp-border);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.rp-card-titulo {
  font-weight: 700;
  margin-bottom: 8px;
}
.rp-card-lista {
  list-style: none;
  margin: 0;
  padding: 0;
}
.rp-card-lista li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 0;
  border-top: 1px solid var(--rp-border);
  font-size: 0.85rem;
}

/* ── Legenda ── */
.rp-legenda {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 16px;
  font-size: 0.8rem;
  color: var(--rp-muted);
}
.rp-legenda > span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}
</style>
